import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { AlertComponent } from 'nebula-ui-kit';
import { VAULT_ALERT_AUTO_DISMISS_MS } from '@nebula/shared-vault/data-access';
import {
  buildVaultPageAlertRenderItems,
  type VaultPageAlertRenderItem,
  type VaultPageAlertType,
} from './vault-page-alert.util';

export type { VaultPageAlertType } from './vault-page-alert.util';

/**
 * Alert de contexto de pantalla (debajo del page-header).
 * Toast derecha = solo acciones puntuales (`VaultAppToastService`).
 */
@Component({
  selector: 'app-vault-page-alert',
  standalone: true,
  imports: [AlertComponent],
  template: `
    @for (item of items(); track item.id) {
      <lib-alert
        [type]="item.type"
        [title]="item.title"
        [message]="item.message"
        [autoClose]="autoClose()"
        [duration]="duration()"
        [closeButton]="closeButton()"
        (closed)="onClosed()"
      />
    }
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VaultPageAlertComponent {
  private readonly host = inject(ElementRef<HTMLElement>);
  private seq = 0;

  readonly type = input<VaultPageAlertType>('info');
  readonly title = input('Aviso');
  readonly message = input<string | null>(null);
  readonly autoClose = input(true);
  readonly duration = input(VAULT_ALERT_AUTO_DISMISS_MS);
  readonly closeButton = input(true);
  readonly scrollIntoViewOnShow = input(true);

  readonly closed = output<void>();

  readonly items = signal<VaultPageAlertRenderItem[]>([]);

  constructor() {
    effect(() => {
      const msg = this.message();
      const type = this.type();
      const title = this.title();
      const next = buildVaultPageAlertRenderItems(msg, type, title, () => ++this.seq);
      this.items.set(next);
      if (next.length > 0 && this.scrollIntoViewOnShow()) {
        queueMicrotask(() => {
          this.host.nativeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        });
      }
    });
  }

  protected onClosed(): void {
    this.items.set([]);
    this.closed.emit();
  }
}
